/**
 * useApiAccessLevel — reads the org's `api_access_level` (none | read | full)
 * from /v1/organization/profile so plan-gated Integrations surfaces (API keys,
 * Webhooks) can BLOCK write actions client-side, not just show a banner.
 *
 * Closed-loop rule (owner 2026-07-02): a plan-restricted action must be 100%
 * blocked AND guide the user to upgrade — never a clickable button that opens a
 * form which then 402s. `canWrite` gates create/revoke; `openUpgrade()` fires
 * the app-wide upgrade dialog (same event the banner + TopNav use).
 */
import { useEffect, useState, useCallback } from 'react';
import { profileApi } from '../api/organization';

export type ApiAccessLevel = 'none' | 'read' | 'full';

export function useApiAccessLevel() {
  // Fail closed until the profile lands. Pages wait for `loaded` before
  // issuing plan-gated list requests, so a Starter/none tenant never creates
  // an expected 402 just because the entitlement request was still in flight.
  const [level, setLevel] = useState<ApiAccessLevel>('none');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    profileApi.get()
      .then((p) => { if (alive) setLevel(((p.api_access_level as ApiAccessLevel) || 'none')); })
      .catch(() => { /* fail closed at 'none'; backend remains the hard authorization backstop */ })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const openUpgrade = useCallback(() => {
    window.dispatchEvent(new CustomEvent('svdy:upgrade:open'));
  }, []);

  return {
    level,
    loaded,
    /** May the tenant create/revoke API keys + webhooks? (write needs 'full') */
    canWrite: level === 'full',
    /** May the tenant view existing keys/webhooks? ('read' or 'full') */
    canRead: level === 'full' || level === 'read',
    openUpgrade,
  };
}

export default useApiAccessLevel;
